import { callProxy } from './supabase';

export interface MinerData {
  wallet_address: string;
  user_id: string;
  created_at: string;
  social_links?: {
    provider: string;
    verified: boolean;
  }[];
  cow_balance?: number;
  bnb_balance?: number;
  account_status?: 'active' | 'blocked' | 'restricted';
}

export interface ActivityLog {
  id: string;
  wallet_address: string;
  activity_type: string;
  description: string;
  status: string;
  tx_hash?: string;
  amount?: number;
  created_at: string;
  metadata?: any;
}

export interface MiningStats {
  totalMiners: number;
  activeMiners24h: number;
  totalCoweMined: number;
  successRate: number;
}

/**
 * Lấy danh sách thợ đào (miners) từ bảng wallets
 */
export async function getMiners(params: {
  current?: number;
  pageSize?: number;
  wallet_address?: string;
  user_id?: string;
  account_status?: string;
}) {
  const { current = 1, pageSize = 10, wallet_address, user_id, account_status } = params;

  try {
    // Fetch wallets and social accounts separately because of missing relationship
    const [allWallets, allSocial] = await Promise.all([
      callProxy({
        action: 'select',
        table: 'wallets',
        select: '*'
      }),
      callProxy({
        action: 'select',
        table: 'linked_social_accounts',
        select: 'user_id, provider, verified'
      })
    ]);

    // Join data in memory
    const walletsWithSocial = (allWallets || []).map((wallet: any) => ({
      ...wallet,
      social_links: (allSocial || []).filter((s: any) => s.user_id === wallet.user_id),
      account_status: wallet.account_status || 'active'
    }));

    let filtered = walletsWithSocial;
    if (wallet_address) filtered = filtered.filter((w: any) => w.wallet_address.toLowerCase().includes(wallet_address.toLowerCase()));
    if (user_id) filtered = filtered.filter((w: any) => w.user_id === user_id);
    if (account_status && account_status !== 'all') filtered = filtered.filter((w: any) => w.account_status === account_status);

    const total = filtered.length;
    const paginated = filtered.slice((current - 1) * pageSize, current * pageSize);

    return {
      data: paginated || [],
      total: total || 0,
      success: true,
    };
  } catch (error) {
    console.error('Error fetching miners:', error);
    return { data: [], total: 0, success: false };
  }
}

/**
 * Lấy chi tiết hoạt động của một thợ đào
 */
export async function getMinerActivities(walletAddress: string) {
  try {
    const data = await callProxy({
      action: 'select',
      table: 'user_activities',
      select: '*',
      eqField: 'wallet_address',
      eqValue: walletAddress.toLowerCase()
    });

    return {
      data: data || [],
      success: true,
    };
  } catch (error) {
    console.error('Error fetching miner activities:', error);
    return { data: [], success: false };
  }
}

/**
 * Lấy tất cả nhật ký hoạt động hệ thống
 */
export async function getAllActivities(params: {
  current?: number;
  pageSize?: number;
  wallet_address?: string;
  type?: string;
  status?: string;
}) {
  const { current = 1, pageSize = 10, wallet_address, type, status } = params;

  try {
    const allActivities = await callProxy({
      action: 'select',
      table: 'user_activities',
      select: '*'
    });

    let filtered = allActivities || [];
    if (wallet_address) filtered = filtered.filter((a: any) => a.wallet_address.toLowerCase().includes(wallet_address.toLowerCase()));
    if (type && type !== 'all') filtered = filtered.filter((a: any) => a.activity_type === type);
    if (status && status !== 'all') filtered = filtered.filter((a: any) => a.status === status);

    const total = filtered.length;
    const paginated = filtered.slice((current - 1) * pageSize, current * pageSize);

    return {
      data: paginated || [],
      total: total || 0,
      success: true,
    };
  } catch (error) {
    console.error('Error fetching all activities:', error);
    return { data: [], total: 0, success: false };
  }
}

/**
 * Lấy chỉ số tổng quan về việc đào COW
 */
export async function getMiningStats(): Promise<MiningStats> {
  try {
    // 1. Tổng số thợ đào
    const wallets = await callProxy({ action: 'select', table: 'wallets', select: 'id' });
    const totalMiners = wallets?.length || 0;

    // 2. Thợ đào hoạt động trong 24h
    const currentActivities = await callProxy({ action: 'select', table: 'user_activities', select: 'wallet_address, created_at, activity_type, status, amount' });
    const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const activeMiners24h = new Set(currentActivities?.filter((a: any) => new Date(a.created_at) >= last24h).map((a: any) => a.wallet_address)).size;

    // 3. Tổng số COW đã đào
    const miningActivities = currentActivities?.filter((a: any) => a.activity_type === 'MINING' && a.status === 'completed') || [];
    const totalCoweMined = miningActivities.reduce((acc: number, curr: any) => acc + (Number(curr.amount) || 0), 0);

    // 4. Tỷ lệ thành công
    const allMining = currentActivities?.filter((a: any) => a.activity_type === 'MINING') || [];
    const successRate = allMining.length ? (miningActivities.length / allMining.length) * 100 : 0;

    return {
      totalMiners: totalMiners || 0,
      activeMiners24h: activeMiners24h,
      totalCoweMined,
      successRate,
    };
  } catch (error) {
    console.error('Error fetching mining stats:', error);
    return {
      totalMiners: 0,
      activeMiners24h: 0,
      totalCoweMined: 0,
      successRate: 0,
    };
  }
}

export interface ApprovalData {
  id: string;
  wallet_address: string;
  action_type: string;
  payload: any;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  created_at: string;
  updated_at: string;
}

/**
 * Lấy danh sách yêu cầu phê duyệt
 */
export async function getPendingApprovals(params: {
  current?: number;
  pageSize?: number;
  wallet_address?: string;
  status?: string;
}) {
  const { current = 1, pageSize = 10, wallet_address, status } = params;

  try {
    const data = await callProxy({
      action: 'select',
      table: 'pending_approvals',
      select: '*'
    });

    let filtered = data || [];
    if (wallet_address) filtered = filtered.filter((a: any) => a.wallet_address.toLowerCase().includes(wallet_address.toLowerCase()));
    if (status && status !== 'all') filtered = filtered.filter((a: any) => a.status === status);

    const total = filtered.length;
    const paginated = filtered.slice((current - 1) * pageSize, current * pageSize);

    return {
      data: paginated || [],
      total: total || 0,
      success: true,
    };
  } catch (error) {
    console.error('Error fetching pending approvals:', error);
    return { data: [], total: 0, success: false };
  }
}

/**
 * Cập nhật trạng thái phê duyệt và thực thi giao dịch ngay tại Admin Side
 */
import { sendSystemNotification } from './notification';

/**
 * Cập nhật trạng thái phê duyệt và thực hiện các hành động kèm theo
 * @param id ID của bản ghi phê duyệt
 * @param status Trạng thái mới (APPROVED/REJECTED)
 * @param walletAddress Địa chỉ ví của người dùng (để mở khóa và gửi thông báo)
 * @param messages Lời nhắn song ngữ (tùy chọn)
 */
export async function updateApprovalStatus(
  id: string, 
  status: 'APPROVED' | 'REJECTED',
  walletAddress?: string,
  messages?: { en: string, vi: string }
) {
  try {
    // 1. Cập nhật trạng thái vào Database (bảng pending_approvals)
    await callProxy({
      action: 'update',
      table: 'pending_approvals',
      data: { 
        status,
        updated_at: new Date().toISOString()
      },
      eqField: 'id',
      eqValue: id
    });

    // 2. Nếu là APPROVED, tự động mở khóa tài khoản người dùng
    if (status === 'APPROVED' && walletAddress) {
      const lockResult = await updateMinerStatus(walletAddress, 'active');
      console.log(`[Admin] Auto-unlock miner ${walletAddress}:`, lockResult.success ? 'SUCCESS' : 'FAILED');
      
      if (!lockResult.success) {
        // Nếu không mở khóa được ví, trả về lỗi để Admin biết
        return { 
          success: false, 
          error: `Đã duyệt yêu cầu nhưng không thể mở khóa ví (${walletAddress}). Vui lòng kiểm tra lại trạng thái ví thủ công.` 
        };
      }
    }

    // 3. Nếu có lời nhắn, gửi thông báo hệ thống song ngữ
    if (messages && walletAddress) {
      const localizedContent = JSON.stringify({
        en: messages.en,
        vi: messages.vi
      });
      await sendSystemNotification([walletAddress], localizedContent);
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Error in updateApprovalStatus:', error);
    return { success: false, error: error.message || 'Lỗi hệ thống khi xử lý phê duyệt' };
  }
}

/**
 * Đếm số lượng yêu cầu đang chờ phê duyệt
 */
export async function getPendingApprovalsCount() {
  try {
    const data = await callProxy({
      action: 'select',
      table: 'pending_approvals',
      select: 'id',
      eqField: 'status',
      eqValue: 'PENDING'
    });
    return data?.length || 0;
  } catch (error) {
    console.error('Error counting pending approvals:', error);
    return 0;
  }
}

export async function updateMinerStatus(walletAddress: string, status: 'active' | 'blocked' | 'restricted') {
  try {
    const result = await callProxy({
      action: 'update',
      table: 'wallets',
      data: { account_status: status },
      eqField: 'wallet_address',
      eqValue: walletAddress
    });
    
    // Kiểm tra xem có bản ghi nào được cập nhật không
    if (!result || (Array.isArray(result) && result.length === 0)) {
        return {
            success: false,
            error: 'Không tìm thấy ví thợ đào phù hợp để cập nhật trạng thái.'
        };
    }

    return {
      success: true,
      data: result
    };
  } catch (error) {
    console.error('Error updating miner status:', error);
    return {
      success: false,
      error
    };
  }
}
